import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getEmpresaById } from "@/modules/empresas/firestore";
import useQueryGroup from "@/modules/etapa-processo/components/estrategia-list/useGetGroup";
import useGetProjetoById from "@/modules/projetos/hooks/useGetProjetoById";
import useUserStore from "@/modules/users/store";
import { getCurrentUser } from "@/services/auth";
import useGetQuestionarioItem from "@/src/routes/responder-questionario-externo-page/hooks/useGetQuestionarioFromUrl";
import type { ReportData, ResultSegment, UseReportDataState } from "../types";

interface UseReportDataParameters {
  questionarioId: string;
  empresaId: string;
  projetoId: string;
  empresaProjetoId: string;
  // Opcionais: passar os objetos já carregados para evitar fetch duplicado
  questionario?: TQuestionario;
  projeto?: TProjeto;
}

/**
 * Input type for resultados - allows undefined values from GrupoNota[]
 */
interface ResultadosInput {
  empresa: Array<{ segment: string; value: number | undefined; autoAvaliacao?: number }>;
  geral?: Array<{ segment: string; value: number | undefined; autoAvaliacao?: number }>;
  categoria?: Array<{ segment: string; value: number | undefined; autoAvaliacao?: number }>;
  primeiroLugar?: Array<{ segment: string; value: number | undefined; autoAvaliacao?: number }>;
}

/**
 * Transform GrupoNota arrays to ResultSegment arrays
 * Filters out entries with undefined values
 * @param input
 */
function transformResultados(
  input: Array<{ segment: string; value: number | undefined; autoAvaliacao?: number }> | undefined
): ResultSegment[] {
  if (!input) return [];
  const result: ResultSegment[] = [];
  for (const item of input) {
    if (item.value !== undefined) {
      result.push({
        segment: item.segment,
        value: item.value,
        autoAvaliacao: item.autoAvaliacao
      });
    }
  }
  return result;
}

/**
 * Hook para consolidar todos os dados necessários para gerar um relatório PDF
 *
 * Integra dados de múltiplas fontes:
 * - Questionário e projeto
 * - Empresa
 * - Grupos de diagnóstico
 * - Resultados do radar (deve ser passado externamente)
 * @param params
 * @param parameters
 * @param resultados
 */
export function useReportData(parameters: UseReportDataParameters, resultados?: ResultadosInput): UseReportDataState {
  const {
    questionarioId,
    empresaId,
    projetoId,
    empresaProjetoId,
    questionario: questionarioParameter,
    projeto: projetoParameter
  } = parameters;

  const licenciadaId = useUserStore((state) => state.activeLicenciadaId);
  const currentUserId = getCurrentUser()?.uid || "";

  // Fetch grupos de diagnóstico
  const { data: gruposData, isFetching: isFetchingGrupos } = useQueryGroup(empresaProjetoId);

  // Fetch projeto (só se não foi passado)
  const { data: projetoFetched, isLoading: isLoadingProjeto } = useGetProjetoById(
    empresaProjetoId,
    projetoId,
    licenciadaId
  );
  const projeto = projetoParameter || projetoFetched;

  // Fetch questionário (só se não foi passado)
  const { data: questionarioFetched, isLoading: isLoadingQuestionario } = useGetQuestionarioItem(
    empresaProjetoId,
    questionarioId
  );
  const questionario = questionarioParameter || questionarioFetched;

  // Fetch empresa
  const {
    data: empresaData,
    isLoading: isLoadingEmpresa,
    error: empresaError
  } = useQuery({
    queryKey: ["empresa-report", empresaId, licenciadaId],
    queryFn: async (): Promise<TEmpresa | null> => {
      if (!empresaId) return null;
      const snapshot = await getEmpresaById(empresaId, licenciadaId);
      if (!snapshot.exists()) return null;
      return snapshot.data();
    },
    enabled: !!empresaId,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  // Filter and sort grupos for radar
  const grupos = useMemo(() => {
    const gruposUnsorted = gruposData ? gruposData.filter((grupo) => grupo.radar) : [];
    return gruposUnsorted.sort((a, b) => a.ordemQuestionario - b.ordemQuestionario);
  }, [gruposData]);

  // Consolidate all data
  const data: ReportData | null = useMemo(() => {
    if (!questionario || !projeto || !empresaData || grupos.length === 0) {
      return null;
    }

    // Transform resultados to filter out undefined values
    const empresaResults = transformResultados(resultados?.empresa);
    const geralResults = transformResultados(resultados?.geral);
    const categoriaResults = transformResultados(resultados?.categoria);
    const primeiroLugarResults = transformResultados(resultados?.primeiroLugar);

    // Debug: Log input and output
    console.log("[useReportData] Input resultados:", {
      empresaInput: resultados?.empresa?.length ?? 0,
      geralInput: resultados?.geral?.length ?? 0,
      categoriaInput: resultados?.categoria?.length ?? 0,
      empresaFirstValue: resultados?.empresa?.[0]?.value,
      geralFirstValue: resultados?.geral?.[0]?.value,
      categoriaFirstValue: resultados?.categoria?.[0]?.value
    });
    console.log("[useReportData] Transformed resultados:", {
      empresaOutput: empresaResults.length,
      geralOutput: geralResults.length,
      categoriaOutput: categoriaResults.length
    });

    // Ensure we have at least empresa results
    if (empresaResults.length === 0) {
      return null;
    }

    return {
      questionarioId,
      empresaId,
      projetoId,
      empresaProjetoId,
      questionario,
      projeto,
      empresa: empresaData,
      grupos,
      resultados: {
        empresa: empresaResults,
        geral: geralResults.length > 0 ? geralResults : undefined,
        categoria: categoriaResults.length > 0 ? categoriaResults : undefined,
        primeiroLugar: primeiroLugarResults.length > 0 ? primeiroLugarResults : undefined
      },
      dataGeracao: new Date(),
      geradoPor: currentUserId
    };
  }, [
    questionario,
    projeto,
    empresaData,
    grupos,
    resultados,
    questionarioId,
    empresaId,
    projetoId,
    empresaProjetoId,
    currentUserId
  ]);

  // Só considera loading se estamos buscando e não temos os dados ainda
  const isLoading =
    isFetchingGrupos ||
    (!projetoParameter && isLoadingProjeto) ||
    (!questionarioParameter && isLoadingQuestionario) ||
    isLoadingEmpresa;
  const error = empresaError instanceof Error ? empresaError : null;

  const refresh = async () => {
    // Trigger refetches if needed
  };

  return {
    data,
    isLoading,
    error,
    refresh
  };
}
